<template>
  <div class="card">
    <div class="card-header">
      <div class="bolder-title"> {{ name || "Рейтинг" }}

        <popper hover content="График изменения рейтинга по доменам">
          <i class="bi bi-info pointer"></i>
        </popper>
      </div>
      <br>
      <span class="help-text"> Данные появляются не сразу. Для формирования истории требуется несколько дней сбора отзывов&nbsp;&nbsp;&nbsp;</span>
    </div>

    <div class="card-body">
      <div class="graph-header">
        <div id="legend-container-1" class="legend">
          <div class="dropdown">
            <ul class="selected">
              <li v-for="resource in resources.filter(x=>x.check)">
                <div class="name">
                  <div class="resource-checkbox">
                    <input type="checkbox"
                           :id="`rating-graph-select-${resource.resource_id}-${metric}`"
                           v-model="resource.check">
                    <label :for="`rating-graph-select-${resource.resource_id}-${metric}`">
                      <span :style="{backgroundColor: resource.color, borderColor: resource.color}" class="check">
                        <i class="bi bi-check"></i>
                      </span>
                      {{ resource.name || resource.domain }}
                    </label>
                  </div>
                </div>
              </li>
            </ul>
            <button class="" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">
              <i class="bi bi-chevron-down"></i>
            </button>
            <ul class="dropdown-menu">
              <li v-for="resource in resources">
                <div class="name">
                  <div class="resource-checkbox">
                    <input type="checkbox"
                           :id="`rating-graph-choice-${resource.resource_id}-${metric}`"
                           v-model="resource.check">
                    <label :for="`rating-graph-choice-${resource.resource_id}-${metric}`">
                      <span :style="{backgroundColor: resource.color, borderColor: resource.color}" class="check">
                        <i class="bi bi-check"></i>
                      </span>
                      {{ resource.name || resource.domain }}
                    </label>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <ul class="nav nav-underline">
          <li v-for="interval in intervals"
              :class="'nav-item' + (interval.value === active_interval?' active':'')">
            <a href=""
               :class="'nav-link' + (interval.value === active_interval?' active':'')"
               @click.prevent="active_interval = interval.value">
              {{ interval.name }}
            </a>
          </li>
        </ul>
      </div>
      <div class="graph">
        <Line
            v-if="chart_data && chart_data.datasets && chart_data.datasets.length"
            :options="chartOptions"
            :data="chart_data"
        />
        <div v-else class="no-data-message">
          Нет данных для отображения
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {Line} from "vue-chartjs";
import {Chart as ChartJS, Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement} from 'chart.js'
import {ReviewApi} from "@/api/reviews";
import datesMixin from "@/mixins/filterDates";
import {externalTooltipHandler} from "@/plugins/chartPlugin";

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement)
ChartJS.defaults.plugins.legend.display = false;


export default {
  name: 'RatingGraph',
  components: {Line},
  props: {
    name: {type: String},
    active_interval_name: {type: String, default: 'day'},
    metric: {type: String, default: 'rating'},
    show_date: {type: Boolean},
    max_show: {type: Number, default: 3},
    max_scale: {type: Number, default: null},
  },

  data() {
    return {
      active_interval: null,
      intervals: [
        {name: "1Д", value: "day"},
        {name: "1Н", value: "week"},
        {name: "1М", value: "month"},
        {name: "3М", value: "quarter"},
        {name: "6М", value: "half_year"},
        {name: "1Г", value: "year"},
      ],
      resources: [],
      componentId: null,
    }
  },
  mixins: [datesMixin],

  watch: {
    'resources': {
      handler() {
        if (this.componentId) {
          const selected = this.resources.filter(r => r.check).map(r => r.resource_id);
          localStorage.setItem(`selected_resources_${this.metric}`, JSON.stringify(selected));
        }
      },
      deep: true
    },
    active_interval() {
      if (this.active_interval) this.getData()
    },
    metric() {
      this.getData()
    }
  },

  mounted() {
    this.active_interval = this.active_interval_name;
    this.componentId = `graph_${Date.now()}_${Math.random()}`; // Генерируем уникальный ID
    this.getData();
  },

  computed: {
    chartOptions() {
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 1,
        layout: {padding: 20},
        elements: {point: {radius: 2}},
        scales: {
          x: {
            ticks: {
              autoSkip: true
            },
            grid: {display: false}
          },
          y: {
            grid: {display: false},
            ticks: {step: 1},
            min: 0,
          },
        },
        interaction: {mode: 'index', intersect: false},
        plugins: {
          legend: {display: false},
          tooltip: {enabled: false, position: 'nearest', external: externalTooltipHandler},
          datalabels: {formatter: () => ''}
        }
      };

      if (this.metric === 'rating' && this.max_scale !== null) {
        options.scales.y.max = this.max_scale;
      } else if (this.metric === 'review_count') {
        options.scales.y.ticks.stepSize = 1;
        options.scales.y.title = {display: true, text: 'Количество отзывов'};
      } else {
        options.scales.y.max = this.max_scale || 5;
        options.scales.y.title = {display: true, text: 'Рейтинг'};
      }

      return options;
    },

    chart_data() {
      let datasets = [];
      let labels = [];

      const checkedResources = this.resources.filter(x => x.check === true);

      if (!checkedResources.length) {
        return {labels: [], datasets: []};
      }

      checkedResources.forEach(resource => {
        if (!labels.length && resource.points && resource.points.length) {
          labels = resource.points.map(x => x.interval);
        }

        let dataField = this.metric === 'review_count' ? 'review_count' : 'rating';

        datasets.push({
          label: resource.name || resource.domain,
          backgroundColor: resource.color,
          borderColor: resource.color,
          type: 'line',
          fill: false,
          lineTension: 0,
          data: resource.points.map(x => x[dataField] !== undefined ? x[dataField] : null)
        });
      });

      return {labels: labels, datasets: datasets};
    }
  },

  methods: {
    async getData() {
      try {
        const response = await ReviewApi.rating_graph(
            this.active_interval,
            this.date_from,
            this.date_to,
            this.metric
        );

        let savedSelected = localStorage.getItem(`selected_resources_${this.metric}`);
        let selectedIds = savedSelected ? JSON.parse(savedSelected) : [];

        this.resources = response.map((x, index) => {
          if (!x.color) {
            x.color = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
          }

          if (selectedIds.length) {
            x.check = selectedIds.includes(x.resource_id);
          } else {
            x.check = index < this.max_show;
          }

          if (this.metric === 'review_count' && x.points) {
            x.points = x.points.map(point => ({
              ...point,
              review_count: point.review_count || 0
            }));
          }

          return x;
        });

      } catch (error) {
        console.error('Error loading chart data:', error);
        this.resources = [];
      }
    }
  }
}
</script>

<style scoped>
.no-data-message {
  text-align: center;
  padding: 50px;
  color: #6C6E79;
  font-size: 14px;
}

.graph {
  height: 330px;
}

</style>

